<ng-template #clearButton>
    <ui-button *ngIf="table.hasClearableFilters()" type="ghost" (click)="onClearFilters()" size="mobile-shrink">
        <mat-icon svgIcon="close"></mat-icon>
        <span class="only-medium">{{ 'table.clearFilters' | appTranslate }}</span>
    </ui-button>
</ng-template>

<!-- The column header appears at the top of the table on desktop and non-responsive tables.
On responsive tables the template will instead enable the headers to be rendered within the vertical row on mobile devices-->
<ng-template #columnHeader let-column="column">
    <div class="column-title-container">
        <ng-container *ngIf="!column.sortable">
            <span class="column-title" [class.filtered]="column.isFiltered()"> {{ column.title | appTranslate }} </span>
        </ng-container>

        <ng-container *ngIf="column.sortable">
            <span class="column-title sortable" [class.filtered]="column.isFiltered()" (click)="onSort(column, $event)">
                {{ column.title | appTranslate }}
            </span>
        </ng-container>

        <div class="sort-spacer" (click)="onSort(column, $event)">
            <ng-container *ngIf="column.sortable && table.sortColumn === column">
                <span class="icon">
                    <mat-icon svgIcon="arrowUp" *ngIf="table.sortDirection === SortDirections.ASC"></mat-icon>
                    <mat-icon svgIcon="arrowDown" *ngIf="table.sortDirection === SortDirections.DESC"></mat-icon>
                </span>
            </ng-container>
        </div>

        <ng-container *ngIf="!showMultiSelectFilter && column.filter">
            <div
                role="button"
                [class.active]="column.isFiltered()"
                [class.open]="showFilterColumn === column"
                (click)="onShowFilter(column)"
                class="filter filter-icon"
                cdkOverlayOrigin
                #trigger="cdkOverlayOrigin"
            >
                <div
                    class="cover"
                    [class.filter]="showFilterColumn === column"
                    [class.show]="showFilterColumn === column"
                ></div>

                <mat-icon [svgIcon]="column.isFiltered() ? 'filterFilled' : 'filter'"></mat-icon>
            </div>

            <ng-template
                cdkConnectedOverlay
                [cdkConnectedOverlayOrigin]="trigger"
                [cdkConnectedOverlayHasBackdrop]="true"
                [cdkConnectedOverlayBackdropClass]="'clear-backdrop'"
                [cdkConnectedOverlayGrowAfterOpen]="true"
                [cdkConnectedOverlayOpen]="showFilterColumn === column"
                (backdropClick)="handleFilterBackdropClick()"
            >
                <app-table-column-filter-popup [filter]="column.filter" />
            </ng-template>
        </ng-container>
    </div>
</ng-template>

<div
    class="wrapper"
    [class.responsive]="isResponsive"
    [class.is-small]="isSmall"
    [class.no-white-space]="noWhiteSpace"
    [class.no-padding]="noPadding"
>
    <div *ngIf="showSearch && showOffset && !isSmall" class="offset-bar"></div>
    <div class="actions-container" [class.shadow-wrapper]="!embedded" [class.sticky]="useStickyActions">
        <div class="actions top sticky" [ngClass]="showActionsPadding ? 'has-padding' : 'padding-bottom-2'">
            <div class="title-container" *ngIf="title">
                <div class="title-contents">
                    <mat-icon
                        [svgIcon]="options?.iconName"
                        [ngStyle]="{ color: options?.iconColor }"
                        *ngIf="options?.iconName"
                    ></mat-icon>
                    <h2>{{ title }}</h2>
                    <ui-badge
                        class="margin-left-1"
                        [type]="options?.badgeType"
                        *ngIf="options?.badgeType && options?.badgeLabel"
                        >{{ options.badgeLabel | appTranslate }}</ui-badge
                    >
                    <div class="only-large" *ngIf="!showSearch">
                        <ng-container [ngTemplateOutlet]="clearButton" />
                    </div>
                </div>
            </div>

            <div class="search-container" *ngIf="showSearch">
                <app-search
                    #searchBar
                    [placeholder]="searchPlaceholder"
                    (onSearch)="onSearch($event)"
                    [initialValue]="table.paginator.search"
                    [minWidth]="searchWidth"
                ></app-search>
                <ng-container [ngTemplateOutlet]="clearButton"></ng-container>
                <ng-content select=".search-buttons"></ng-content>
            </div>

            <div class="filters">
                <ng-container *ngIf="showMultiSelectFilter; else singleSelect">
                    <app-table-multi-filter [filters]="getAllFilters()"> </app-table-multi-filter>
                </ng-container>
                <ng-template #singleSelect>
                    <ng-container *ngFor="let filter of table.filters" [ngSwitch]="filter.type">
                        <app-table-select-filter *ngSwitchCase="'select'" [filter]="filter"> </app-table-select-filter>
                    </ng-container>
                </ng-template>
            </div>

            <div class="hide-large" *ngIf="title && !showSearch">
                <ng-container [ngTemplateOutlet]="clearButton" />
            </div>

            <app-pagination
                *ngIf="showPagination && paginationPosition === 'top'"
                [total]="table.paginator.total"
                [current]="table.paginator.page"
                [limit]="table.paginator.pageSize"
                (pageChange)="table.paginator.page = $event"
                [disableLoad]="isLoading"
            >
            </app-pagination>

            <!-- This code allows for adding buttons next to the paginator -->
            <!-- Create a div inside the app-table with the "buttons" class and put your ui-buttons inside -->
            <!-- Ex: <app-table><div class="buttons"><ui-button>Click me</ui-button></div></app-table> -->
            <div class="button-container margin-left-2">
                <ng-content select=".buttons"></ng-content>
            </div>
        </div>

        <div *ngIf="subTitle" class="sub-title">
            <p>{{ subTitle }}</p>
        </div>

        <div class="table-wrapper">
            <div *ngIf="isLoading && showLoading" class="ui active inverted dimmer">
                <div class="ui loader"></div>
            </div>
            <table [class.disabled-clicks]="showFilterColumn">
                <thead class="sticky" *ngIf="showHeader && (!isResponsive || !isMobile)">
                    <tr>
                        <th *ngIf="table.bulkSelect" class="sticky">
                            <div>
                                <span class="column-title bulk-select-all-checkbox">
                                    <mat-checkbox
                                        (change)="onAllSelectChanged($event.checked)"
                                        [checked]="bulkSelection.isAllSelected"
                                        [disabled]="readonly"
                                    ></mat-checkbox>
                                </span>
                            </div>
                        </th>
                        <th
                            *ngFor="let column of table.columns"
                            [style.width]="table.columnWidths.get(column)"
                            [class.has-filter-open]="showFilterColumn === column"
                            class="sticky"
                        >
                            <ng-container
                                [ngTemplateOutlet]="columnHeader"
                                [ngTemplateOutletContext]="{ column: column }"
                            >
                            </ng-container>
                        </th>

                        <!-- Collapsed cell header for meatball menu -->
                        <th class="sticky" *ngIf="table.showMenuColumnOptions"></th>
                    </tr>

                    <!-- These act as the bottom borders -->
                    <tr aria-hidden="true">
                        <th
                            *ngIf="table.bulkSelect"
                            class="bottom-border-row bulk-select-column"
                            aria-hidden="true"
                        ></th>
                        <th
                            *ngFor="let column of table.columns"
                            [style.width]="table.columnWidths.get(column)"
                            aria-hidden="true"
                            class="bottom-border-row"
                        ></th>
                        <th aria-hidden="true" *ngIf="table.showMenuColumnOptions" class="bottom-border-row"></th>
                    </tr>
                </thead>

                <tbody
                    class="app-table-body"
                    [class.empty]="showEmptyState"
                    [class.links]="table.links || table.unnavigableLink"
                >
                    <tr
                        *ngFor="let row of table.paginator?.values; trackBy: trackByFunction; let rowIndex = index"
                        [attr.tabindex]="rowIndex + 1"
                        (keydown.enter)="onRowClicked(row, $event); onNavigate(row)"
                        [attr.id]="'app-table-row-' + row.id"
                        [attr.data-id]="row.id"
                        class="app-table-row"
                        (click)="onRowClicked(row, $event)"
                        appDraggable
                        [dragEnabled]="table.allowDragAndDrop"
                        [draggableData]="getDraggedData(row)"
                        (dragStart)="dragStart($event)"
                        appDroppable
                        [dropEnabled]="table.allowDragAndDrop && isRowValidDropZone(row)"
                        (onDroppableComplete)="onDrop($event, row)"
                        [class.hasBulkSelect]="table.bulkSelect"
                        [class.hasColumnOptions]="table.showMenuColumnOptions"
                        [class.disabled]="isRowDisabled(row)"
                    >
                        <td
                            *ngIf="table.bulkSelect"
                            class="bulk-select-column"
                            [style.grid-row]="'1 / span ' + table.columns.length"
                        >
                            <div
                                class="bulk-select-checkbox"
                                [class.alignGrippyWithCheckbox]="table.showGrippyOnCheckbox && isRowChecked(row)"
                            >
                                <!-- Display grippy icon on hover of row within table -->
                                <div class="grippy">
                                    <mat-icon class="icon" svgIcon="grippy"></mat-icon>
                                </div>
                                <mat-checkbox
                                    (click)="$event.stopPropagation()"
                                    (change)="onRowSelectChanged(row, $event.checked)"
                                    [checked]="isRowChecked(row)"
                                    [disabled]="readonly"
                                ></mat-checkbox>
                            </div>
                        </td>

                        <td
                            *ngFor="let column of table.columns; let first = first; trackBy: trackByFunction"
                            [style.width]="table.columnWidths.get(column)"
                            [class.cell-blur]="column.displayModification.obscure"
                            [class.first]="first"
                        >
                            <div *ngIf="isResponsive && isMobile">
                                <ng-container
                                    [ngTemplateOutlet]="columnHeader"
                                    [ngTemplateOutletContext]="{ column: column }"
                                >
                                </ng-container>
                            </div>
                            <ng-container [ngSwitch]="column.type">
                                <ng-container *ngSwitchCase="cellDisplay.avatar">
                                    <a
                                        [ngClass]="{
                                            'flex-center-aligned':
                                                column.getBadgeDisplay && column.getBadgeDisplay(row),
                                        }"
                                        tabindex="-1"
                                        [routerLink]="getLink(row)"
                                        [queryParams]="getParams(row)"
                                    >
                                        <app-avatar
                                            [employee]="column.getDisplayPropertyValue(row)"
                                            [name]="true"
                                            [size]="column?.size || defaultIconSize"
                                            [style]="
                                                '--column-width: ' +
                                                percentageStringToDecimal(table.columnWidths.get(column))
                                            "
                                        >
                                        </app-avatar>
                                        <ng-container *ngIf="column.getBadgeDisplay && column.getBadgeDisplay(row)">
                                            <ui-badge [type]="column.getBadgeDisplay(row).type">
                                                {{ column.getBadgeDisplay(row).label | appTranslate }}</ui-badge
                                            >
                                        </ng-container>
                                    </a>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.tooltip">
                                    <a
                                        tab-index="-1"
                                        [routerLink]="getLink(row)"
                                        class="tooltip-column"
                                        [queryParams]="getParams(row)"
                                    >
                                        {{ column.getDisplayPropertyValue(row) | appTranslate }}

                                        <ui-info
                                            *ngIf="column.getTooltip(row)"
                                            [text]="column.getTooltip(row)"
                                        ></ui-info>
                                    </a>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.colorCircle">
                                    <a
                                        class="color-circle"
                                        tabindex="-1"
                                        [routerLink]="getLink(row)"
                                        [queryParams]="getParams(row)"
                                    >
                                        <div class="donut" [ngStyle]="column.getRowStyling(row)"></div>
                                        <div>{{ column.getDisplayPropertyValue(row) }}</div>
                                    </a>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.avatarOnly">
                                    <a tabindex="-1" [routerLink]="getLink(row)" [queryParams]="getParams(row)">
                                        <app-avatar [employee]="column.getDisplayPropertyValue(row)" [name]="false">
                                        </app-avatar>
                                    </a>
                                </ng-container>

                                <!-- single avatar with name if one employee and multi avatar if more than 1 employee -->
                                <ng-container *ngSwitchCase="cellDisplay.singleAndMultiAvatar">
                                    <a
                                        [ngStyle]="column.getRowStyling(row)"
                                        tabindex="-1"
                                        [routerLink]="getLink(row)"
                                        [queryParams]="getParams(row)"
                                    >
                                        <div class="multi-avatars">
                                            <ng-container *ngIf="!column.getEmployees(row).length">
                                                {{ column.getDisplayPropertyValue(row) | appTranslate }}
                                            </ng-container>

                                            <ng-container *ngIf="column.getEmployees(row).length">
                                                <app-inline-avatars
                                                    [numberOfEmployeesToShow]="column.getMaximumEmployeesToShow(row)"
                                                    [showEmployeeName]="column.getEmployees(row).length <= 1"
                                                    [employees]="column.getEmployees(row)"
                                                    [withMargins]="false"
                                                    [size]="column?.size || defaultIconSize"
                                                >
                                                </app-inline-avatars>
                                            </ng-container>
                                        </div>
                                    </a>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.multiAvatar">
                                    <a tabindex="-1" [routerLink]="getLink(row)" [queryParams]="getParams(row)">
                                        <div class="multi-avatars">
                                            <app-avatar
                                                *ngFor="let employee of column.getEmployeesToShow(row)"
                                                [employee]="employee"
                                                [showTitle]="false"
                                                [name]="false"
                                                [matTooltip]="employee.fullName"
                                            ></app-avatar>
                                            <span *ngIf="column.hasOverflow(row)"
                                                >+&nbsp;{{ column.overflowCount(row) }}</span
                                            >
                                        </div>
                                    </a>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.string">
                                    <a
                                        [ngStyle]="column.getRowStyling(row)"
                                        tabindex="-1"
                                        [routerLink]="getLink(row)"
                                        [queryParams]="getParams(row)"
                                    >
                                        <ng-container *ngIf="column?.hasIcon && column.getIcon(row)">
                                            <mat-icon
                                                [ngStyle]="column.getIconStyle(row)"
                                                class="icon drop-hover-highlight"
                                                [svgIcon]="column.getIcon(row)"
                                            ></mat-icon>
                                        </ng-container>
                                        <span class="drop-hover-highlight">{{
                                            column.getDisplayPropertyValue(row) | appTranslate
                                        }}</span>
                                        <ng-container *ngIf="column.getBadgeDisplay && column.getBadgeDisplay(row)">
                                            <ui-badge [type]="column.getBadgeDisplay(row).type">
                                                {{ column.getBadgeDisplay(row).label | appTranslate }}</ui-badge
                                            >
                                        </ng-container>
                                    </a>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.bubbleWithText">
                                    <a
                                        class="flex-center-aligned"
                                        tabindex="-1"
                                        [routerLink]="getLink(row)"
                                        [queryParams]="getParams(row)"
                                    >
                                        <span
                                            [ngStyle]="{ 'background-color': column.getBubbleDisplayColor(row) }"
                                            class="bubble"
                                        ></span>
                                        {{ column.getDisplayPropertyValue(row) | appTranslate }}
                                    </a>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.date">
                                    <a
                                        class="date-column"
                                        tabindex="-1"
                                        [routerLink]="getLink(row)"
                                        [queryParams]="getParams(row)"
                                    >
                                        <!-- Reference to ticket https://gethumi.atlassian.net/browse/BUG-9788, To make date consistent across different timezones, example use :  {{ someDate | appDate: 'medium' : 'UTC' }}
                                        If we don't provide a timezone parameter, Angular's DatePipe uses the local timezone of the user's browser for date formatting
                                        i.e, the DatePipe looks for the value using the DATE_PIPE_DEFAULT_OPTIONS injection token (and reads the timezone property).
                                        If the token is not configured, the end-user's local system timezone is used as a value. -->

                                        <span class="drop-hover-highlight">
                                            {{ column.getDisplayPropertyValue(row) | appDate: 'mediumDate' }}
                                        </span>
                                    </a>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.dateTime">
                                    <a
                                        class="date-time-column"
                                        tabindex="-1"
                                        [routerLink]="getLink(row)"
                                        [queryParams]="getParams(row)"
                                    >
                                        {{
                                            column.getDisplayPropertyValue(row)
                                                ? (column.getDisplayPropertyValue(row) | appDate: 'medium')
                                                : '-'
                                        }}
                                    </a>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.dollar">
                                    <a tabindex="-1" [routerLink]="getLink(row)" [queryParams]="getParams(row)">
                                        {{ column.getDisplayPropertyValue(row) | i18nNum: '$' }}
                                    </a>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.percentage">
                                    <a tabindex="-1" [routerLink]="getLink(row)" [queryParams]="getParams(row)">
                                        {{
                                            column.getDisplayPropertyValue(row)
                                                ? (column.getDisplayPropertyValue(row) | i18nNum: '%')
                                                : '-'
                                        }}
                                    </a>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.externalLink">
                                    <ng-container *ngIf="column.getDisplayPropertyValue(row)">
                                        <ui-button
                                            type="link"
                                            tabindex="-1"
                                            [url]="column.getDisplayPropertyValue(row)"
                                            [externalLink]="true"
                                        >
                                            {{ 'view' | translate }}
                                        </ui-button>
                                    </ng-container>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.internalLink">
                                    <a class="link" [routerLink]="column.getInternalLink(row)">
                                        {{ column.getDisplayPropertyValue(row) | appTranslate }}
                                    </a>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.rating">
                                    <ui-star-rating
                                        [(ngModel)]="row[column.getBindProperty(row)]"
                                        (ngModelChange)="onRowChange(column, row)"
                                        [maxStars]="5"
                                    >
                                    </ui-star-rating>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.dropdown">
                                    <mat-form-field
                                        class="no-labels no-helpers"
                                        (click)="$event.stopPropagation(); $event.preventDefault()"
                                    >
                                        <mat-select
                                            [(ngModel)]="row[column.getBindProperty(row)]"
                                            (ngModelChange)="onRowChange(column, row); onDropdownSelection(row, column)"
                                            [disabled]="readonly"
                                        >
                                            <mat-option
                                                *ngFor="
                                                    let dropdownOption of column.getDropdownOptions(row);
                                                    trackBy: trackByFunction
                                                "
                                                [value]="dropdownOption.value"
                                            >
                                                {{ dropdownOption.label | appTranslate }}
                                            </mat-option>
                                        </mat-select>
                                    </mat-form-field>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.rateSelect">
                                    <div
                                        class="app-grid half-gutter"
                                        (click)="$event.stopPropagation(); $event.preventDefault()"
                                    >
                                        <mat-form-field class="columns-6 no-labels no-helpers">
                                            <input
                                                matInput
                                                [(ngModel)]="row[column.getBindProperty(row)]"
                                                (ngModelChange)="onRowChange(column, row)"
                                                [disabled]="readonly"
                                            />
                                        </mat-form-field>
                                        <mat-form-field class="columns-4 no-labels no-helpers">
                                            <mat-select
                                                [(ngModel)]="row[column.getSecondaryBindProperty(row)]"
                                                (ngModelChange)="onRowChange(column, row)"
                                                [disabled]="readonly"
                                            >
                                                <mat-option
                                                    *ngFor="
                                                        let dropdownOption of column.getDropdownOptions(row);
                                                        trackBy: trackByFunction
                                                    "
                                                    [value]="dropdownOption.value"
                                                >
                                                    {{ dropdownOption.label }}
                                                </mat-option>
                                            </mat-select>
                                        </mat-form-field>
                                    </div>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.datepicker">
                                    <mat-form-field
                                        class="no-labels no-helpers"
                                        (click)="$event.stopPropagation(); $event.preventDefault()"
                                    >
                                        <input
                                            matInput
                                            name="datepicker"
                                            #datePickerInput
                                            [(ngModel)]="row[column.getBindProperty(row)]"
                                            (ngModelChange)="onRowChange(column, row)"
                                            [matDatepicker]="picker"
                                            [disabled]="readonly"
                                        />
                                        <mat-datepicker-toggle matSuffix [for]="picker">
                                            <mat-icon matDatepickerToggleIcon svgIcon="calendar"></mat-icon>
                                        </mat-datepicker-toggle>
                                        <mat-datepicker #picker></mat-datepicker>

                                        <mat-error *ngIf="datePickerInput.invalid">{{
                                            datePickerInput.errors | formErrors
                                        }}</mat-error>
                                    </mat-form-field>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.badge">
                                    <a tabindex="-1" [routerLink]="getLink(row)" [queryParams]="getParams(row)">
                                        <span
                                            class="badge-cell-text drop-hover-highlight"
                                            *ngIf="column.getCellText(row)"
                                        >
                                            {{ column.getCellText(row) | appTranslate }}
                                        </span>
                                        <ui-badge
                                            *ngIf="column.getBadgeDisplay(row)"
                                            [type]="column.getBadgeDisplay(row).type"
                                            >{{ column.getBadgeDisplay(row).label | appTranslate }}
                                        </ui-badge>
                                    </a>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.multiBadge">
                                    @for (badge of column.getMultiBadgeDisplay(row); track $index) {
                                        <ui-badge [type]="badge.type">{{ badge.label | translate }}</ui-badge>
                                    }
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.sentiment">
                                    <a tabindex="-1" [routerLink]="getLink(row)" [queryParams]="getParams(row)">
                                        <span *ngIf="row.positive">👍</span>
                                        <span *ngIf="row.constructive">✏</span>
                                        <span *ngIf="row.negative">📋</span>
                                    </a>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.inputText">
                                    <mat-form-field>
                                        <input
                                            matInput
                                            type="text"
                                            name="inputText"
                                            #inputText="ngModel"
                                            [(ngModel)]="row[column.getBindProperty(row)]"
                                            (ngModelChange)="onRowChange(column, row)"
                                            placeholder="{{ column.getPlaceHolderText(row) }}"
                                            [errorStateMatcher]="column.errorStateMatcher"
                                        />
                                        <mat-error *ngIf="inputText.invalid">{{
                                            inputText.errors | formErrors
                                        }}</mat-error>
                                    </mat-form-field>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.inputNumber">
                                    <mat-form-field>
                                        <input
                                            matInput
                                            type="number"
                                            name="inputNumber"
                                            #inputNumber="ngModel"
                                            [(ngModel)]="row[column.getBindProperty(row)]"
                                            (ngModelChange)="onRowChange(column, row)"
                                            placeholder="{{ column.getPlaceHolderText(row) }}"
                                            [errorStateMatcher]="column.errorStateMatcher"
                                        />
                                        <mat-error *ngIf="inputNumber.invalid">{{
                                            inputNumber.errors | formErrors
                                        }}</mat-error>
                                    </mat-form-field>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.textArea">
                                    <mat-form-field style="width: 60%">
                                        <textarea
                                            #textArea="ngModel"
                                            matInput
                                            rows="2"
                                            maxlength="1000"
                                            type="text"
                                            name="inputTextArea"
                                            (ngModelChange)="onRowChange(column, row)"
                                            [(ngModel)]="row[column.getBindProperty(row)]"
                                            [errorStateMatcher]="column.errorStateMatcher"
                                        ></textarea>
                                        <mat-error *ngIf="textArea.invalid">{{
                                            textArea.errors | formErrors
                                        }}</mat-error>
                                    </mat-form-field>
                                </ng-container>

                                <ng-container *ngSwitchCase="cellDisplay.button">
                                    <div
                                        class="button-column"
                                        [matTooltip]="
                                            column.disabled(row) ? (column.disabledText(row) | translate) : null
                                        "
                                    >
                                        <ui-button
                                            *ngIf="column.showIf(row)"
                                            [type]="column.buttonType"
                                            [size]="column.buttonSize"
                                            [disabled]="column.disabled(row)"
                                            tabindex="-1"
                                            (click)="$event.stopPropagation(); onAction(column.getEvent(row), row)"
                                            >{{ column.getButtonText(row) | appTranslate }}</ui-button
                                        >
                                    </div>
                                </ng-container>
                            </ng-container>

                            <!-- show/hide save progress icon -->
                            <ng-container *ngIf="column.showSaveActionIcons">
                                <ng-container
                                    [ngTemplateOutlet]="displayRowChangeStatusIcon"
                                    [ngTemplateOutletContext]="{ rowChangeStatus: row.saveStatus }"
                                >
                                </ng-container>
                            </ng-container>
                        </td>
                        <td
                            class="menu-column-options"
                            *ngIf="table.showMenuColumnOptions"
                            [style.grid-row]="'1 / span ' + table.columns.length"
                            [class.empty]="!hasOptionsColumn"
                        >
                            <ng-container *ngIf="table.getMenuColumnOptionsForRow(row)?.length">
                                <!-- If there's only one option for the row, we show a button, unless if the forceMeatballsDisplay flag is set-->
                                <!-- If there's more than one option for the row or if the forceMeatballsDisplay flag is set, we show meatballs -->
                                <ng-container
                                    *ngIf="
                                        table.getMenuColumnOptionsForRow(row).length === 1 &&
                                            !table.getMenuColumnOptionsForRow(row)[0].forceMeatballsDisplay;
                                        else meatballs
                                    "
                                >
                                    <ui-button
                                        type="link"
                                        (click)="
                                            $event.stopPropagation();
                                            onMenuItemSelected(table.getMenuColumnOptionsForRow(row)[0], row)
                                        "
                                        [class.event-button]="
                                            !!eventIcons[table.getMenuColumnOptionsForRow(row)[0].event]
                                        "
                                    >
                                        <span>{{ table.getMenuColumnOptionsForRow(row)[0].label | appTranslate }}</span>
                                        <mat-icon
                                            class="d-none"
                                            svgIcon="{{ eventIcons[table.getMenuColumnOptionsForRow(row)[0].event] }}"
                                        ></mat-icon>
                                    </ui-button>
                                </ng-container>

                                <ng-template #meatballs>
                                    <button
                                        (click)="$event.stopPropagation()"
                                        class="material-meatball-menu-button drop-hover-highlight"
                                        [matMenuTriggerFor]="menu"
                                    >
                                        <mat-icon svgIcon="meatballHorizontal"></mat-icon>
                                    </button>

                                    <mat-menu #menu="matMenu" xPosition="before">
                                        <span
                                            mat-menu-item
                                            *ngFor="let item of table.getMenuColumnOptionsForRow(row)"
                                            (click)="onMenuItemSelected(item, row)"
                                            >{{ item.label | appTranslate }}</span
                                        >
                                    </mat-menu>
                                </ng-template>
                            </ng-container>
                        </td>
                    </tr>

                    <tr *ngIf="showEmptyState" [@fadeIn]>
                        <td [attr.colspan]="table.bulkSelect ? table.columns.length + 1 : table.columns.length">
                            <ng-content></ng-content>
                        </td>
                    </tr>
                </tbody>
                <ng-content select=".table-footer"></ng-content>
            </table>
            <div class="actions bottom">
                <ng-content select=".footer-buttons"></ng-content>
                <app-pagination
                    *ngIf="showPagination && paginationPosition === 'bottom'"
                    [total]="table.paginator.total"
                    [current]="table.paginator.page"
                    [limit]="table.paginator.pageSize"
                    (pageChange)="table.paginator.page = $event"
                >
                </app-pagination>
            </div>
        </div>
    </div>
</div>

<ng-template #displayRowChangeStatusIcon let-rowChangeStatus="rowChangeStatus">
    <ng-container [ngSwitch]="rowChangeStatus">
        <!-- In progress -->
        <ng-container *ngSwitchCase="rowChangeStatusTypes.saving_in_progress">
            <mat-icon class="margin-left-1 running" svgIcon="loading"></mat-icon>
            Saving...
        </ng-container>

        <!-- Saved-->
        <ng-container *ngSwitchCase="rowChangeStatusTypes.saved">
            <span class="save-progress-icon">
                <mat-icon class="margin-left-1" svgIcon="check"></mat-icon>
                Saved!
            </span>
        </ng-container>

        <!-- Save failed -->
        <ng-container *ngSwitchCase="rowChangeStatusTypes.error_while_saving">
            <span>
                <mat-icon class="margin-left-1 failed" svgIcon="error"></mat-icon>
                Save failed. Please re-enter the value.
            </span>
        </ng-container>
    </ng-container>
</ng-template>
